import ImageFullWidth from '@components/ImageFullWidth';
import ScrollInView from '@utility/ScrollInView/index';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import SwiperCore, { Keyboard, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import * as styles from './styles';

SwiperCore.use([Keyboard, Navigation, Pagination]);

const ImageCarousel = ({ data: { images } }) => {
  const ref = useRef(null);
  const [swiperInstance, setSwiperInstance] = useState(null);

  const params = {
    slidesPerView: 1,
    speed: 1000,
    keyboard: {
      enabled: true,
    },
    loop: true,
    simulateTouch: false,
    navigation: {
      nextEl: '.swiper-next',
      prevEl: '.swiper-prev',
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    watchSlidesProgress: true,
  };

  // Add eventlisteners for swiper after initializing
  useEffect(() => {
    if (!!swiperInstance && typeof window !== 'undefined') {
      swiperInstance.on('resize', () => {
        swiperInstance.update();
      });
      // if larger screen change transition to reveal
      if (matchMedia('(min-width: 1024px)').matches) {
        swiperInstance.once('transitionStart', () => {
          const $wrapperEl = swiperInstance.$wrapperEl;
          const params = swiperInstance.params;
          $wrapperEl
            .children(`.${params.slideClass}.${params.slideDuplicateClass}`)
            .each(function () {
              const idx = this.getAttribute('data-swiper-slide-index');
              this.innerHTML = $wrapperEl
                .children(
                  '.' +
                    params.slideClass +
                    '[data-swiper-slide-index="' +
                    idx +
                    '"]:not(.' +
                    params.slideDuplicateClass +
                    ')',
                )
                .html();
            });
        });

        swiperInstance.on('progress', () => {
          const interleaveOffset = 0.5;
          for (let i = 0; i < swiperInstance.slides.length; i += 1) {
            const slideProgress = swiperInstance.slides[i].progress;
            const innerOffset = swiperInstance.width * interleaveOffset;
            const innerTranslate = slideProgress * innerOffset;
            swiperInstance.slides[i].querySelector(
              '.js-swiper-slide-inner',
            ).style.transform = `translate3d(${innerTranslate}px, 0, 0)`;
          }
        });

        swiperInstance.on('transitionEnd', () => {
          swiperInstance.slideToLoop(swiperInstance.realIndex, 0, false);

          for (let i = 0; i < swiperInstance.slides.length; i += 1) {
            swiperInstance.slides[i].style.transition = '';
            swiperInstance.slides[i].querySelector(
              '.js-swiper-slide-inner',
            ).style.transition = '';
          }
        });

        swiperInstance.on('setTransition', () => {
          for (let i = 0; i < swiperInstance.slides.length; i += 1) {
            swiperInstance.slides[
              i
            ].style.transition = `${swiperInstance.originalParams.speed}ms`;
            swiperInstance.slides[i].querySelector(
              '.js-swiper-slide-inner',
            ).style.transition = `${swiperInstance.originalParams.speed}ms`;
          }
        });
      }
    }

    return () => {
      if (ref.current?.swiper) {
        swiperInstance.off('progress');
        swiperInstance.off('resize');
        swiperInstance.off('update');
        swiperInstance.off('touchStart');
        swiperInstance.off('setTransition');
      }
    };
  }, [swiperInstance]);

  return (
    <ScrollInView className={styles.imageCarousel}>
      <Swiper {...params} onSwiper={(instance) => setSwiperInstance(instance)}>
        {images &&
          images.map((el) => (
            <SwiperSlide key={el._key}>
              <div className="js-swiper-slide-inner slide-inner">
                <ImageFullWidth
                  data={{ image: el }}
                  animateIn={false}
                  loading="eager"
                  parallax={false}
                />
              </div>
            </SwiperSlide>
          ))}
        <div className="swiper-prev" data-context="Previous" />
        <div className="swiper-next" data-context="Next" />
        <div className="swiper-pagination"></div>
      </Swiper>
    </ScrollInView>
  );
};

ImageCarousel.propTypes = {
  data: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default ImageCarousel;
